/**
 * 기준정보 — 기관계좌 · 기관카드
 *
 * - 계좌: 계좌번호 SSOT · 용도 단일 · 부서 N · 사업 N (각각 null/빈배열 = 제한 없음)
 * - 카드: accountUid 참조 · 부서/사업은 연결 계좌에서 조회
 * - API(isBdApiEnabled): CmOrgFinance · 아니면 localStorage
 *
 * 키: erp.master.orgAccount|{tenant}
 *     erp.master.orgCard|{tenant}
 */

import * as XLSX from 'xlsx';
import { isBdApiEnabled } from 'config/bdApiConfig';
import {
  cacheGet,
  cacheSet,
  commonSaveList,
  commonSelect,
  diffRowsByUid,
  parseJsonArray,
} from 'services/commonQuery';

const orgAccountCache = new Map();
const orgCardCache = new Map();

export const SAVE_QUERY_ORG_ACCOUNT = [
  'CmOrgFinance.insertOrgAccount',
  'CmOrgFinance.updateOrgAccount',
  'CmOrgFinance.deleteOrgAccount',
];
export const SAVE_QUERY_ORG_CARD = [
  'CmOrgFinance.insertOrgCard',
  'CmOrgFinance.updateOrgCard',
  'CmOrgFinance.deleteOrgCard',
];

function toJsonArrayParam(arr) {
  return JSON.stringify(Array.isArray(arr) ? arr : []);
}

function mapOrgAccountFromApi(row) {
  const depts = parseJsonArray(row.deptCodesJson ?? row.deptCodes);
  const deptNames = parseJsonArray(row.deptNamesJson ?? row.deptNames);
  const projects = parseJsonArray(row.projectCodesJson ?? row.projectCodes);
  const projectNames = parseJsonArray(row.projectNamesJson ?? row.projectNames);
  return migrateAccountShape({
    ...row,
    deptCodes: depts,
    deptNames,
    projectCodes: projects,
    projectNames,
    mod: '',
  });
}

function mapOrgAccountToApi(row) {
  const depts = normalizeCodeNameLists(row.deptCodes, row.deptNames);
  const projects = normalizeCodeNameLists(row.projectCodes, row.projectNames);
  return {
    ...row,
    accountNo: normalizeAccountNo(row.accountNo),
    deptCodes: depts.codes,
    deptNames: depts.names,
    projectCodes: projects.codes,
    projectNames: projects.names,
    deptCodesJson: toJsonArrayParam(depts.codes),
    deptNamesJson: toJsonArrayParam(depts.names),
    projectCodesJson: toJsonArrayParam(projects.codes),
    projectNamesJson: toJsonArrayParam(projects.names),
  };
}

export const ORG_ACCOUNT_STORAGE_PREFIX = 'erp.master.orgAccount';
export const ORG_CARD_STORAGE_PREFIX = 'erp.master.orgCard';
/** @deprecated 레거시 스코프 — 로드 시 계좌 배열로 이관 후 미사용 */
export const ORG_FINANCE_SCOPE_PREFIX = 'erp.master.orgFinanceScope';

export const ACCOUNT_PURPOSE = {
  INCOME: 'INCOME',
  EXPENSE: 'EXPENSE',
  DEPOSIT: 'DEPOSIT',
  INOUT: 'INOUT',
  COMMON: 'COMMON',
  ETC: 'ETC',
};

export const ACCOUNT_PURPOSE_OPTIONS = [
  { value: ACCOUNT_PURPOSE.INCOME, label: '수입' },
  { value: ACCOUNT_PURPOSE.EXPENSE, label: '지출' },
  { value: ACCOUNT_PURPOSE.DEPOSIT, label: '세외현금' },
  { value: ACCOUNT_PURPOSE.INOUT, label: '입출금' },
  { value: ACCOUNT_PURPOSE.COMMON, label: '공통' },
  { value: ACCOUNT_PURPOSE.ETC, label: '기타' },
];

export const EXPENSE_PURPOSE_SET = new Set([
  ACCOUNT_PURPOSE.EXPENSE,
  ACCOUNT_PURPOSE.INOUT,
  ACCOUNT_PURPOSE.COMMON,
]);

export const INCOME_PURPOSE_SET = new Set([
  ACCOUNT_PURPOSE.INCOME,
  ACCOUNT_PURPOSE.INOUT,
  ACCOUNT_PURPOSE.COMMON,
]);

export const CARD_TYPE = {
  CREDIT: 'CREDIT',
  CHECK: 'CHECK',
  DEBIT: 'DEBIT',
  PREPAID: 'PREPAID',
  ETC: 'ETC',
};

export const CARD_TYPE_OPTIONS = [
  { value: CARD_TYPE.CREDIT, label: '신용카드' },
  { value: CARD_TYPE.CHECK, label: '체크카드' },
  { value: CARD_TYPE.DEBIT, label: '직불카드' },
  { value: CARD_TYPE.PREPAID, label: '선불카드' },
  { value: CARD_TYPE.ETC, label: '기타' },
];

/**
 * 카드 사용용도(사용목적) — 결의 시 승인내역 그룹 필터 키
 * PoC는 상수 옵션 · 추후 공통코드 그룹(ORG_CARD_USAGE)으로 치환
 */
export const CARD_USAGE_CODE_GROUP = 'ORG_CARD_USAGE';

export const CARD_USAGE = {
  PROJECT: 'PROJECT',
  ADMIN: 'ADMIN',
  FUEL: 'FUEL',
  ENTERTAIN: 'ENTERTAIN',
  ETC: 'ETC',
};

export const CARD_USAGE_OPTIONS = [
  { value: CARD_USAGE.PROJECT, label: '사업비' },
  { value: CARD_USAGE.ADMIN, label: '관리비' },
  { value: CARD_USAGE.FUEL, label: '유류비' },
  { value: CARD_USAGE.ENTERTAIN, label: '접대비' },
  { value: CARD_USAGE.ETC, label: '기타' },
];

export const YN_OPTIONS = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
];

function labelOf(options, code) {
  return options.find((o) => o.value === code)?.label || code || '';
}

export function accountPurposeLabel(code) {
  return labelOf(ACCOUNT_PURPOSE_OPTIONS, code);
}

export function cardTypeLabel(code) {
  return labelOf(CARD_TYPE_OPTIONS, code);
}

export function cardUsageLabel(code) {
  return labelOf(CARD_USAGE_OPTIONS, code);
}

export function createAccountUid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `OACC-${crypto.randomUUID()}`;
  }
  return `OACC-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

export function createCardUid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `OCARD-${crypto.randomUUID()}`;
  }
  return `OCARD-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) ?? fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value ?? []));
}

export function orgAccountKey(tenantCode) {
  return `${ORG_ACCOUNT_STORAGE_PREFIX}|${tenantCode || ''}`;
}

export function orgCardKey(tenantCode) {
  return `${ORG_CARD_STORAGE_PREFIX}|${tenantCode || ''}`;
}

export function normalizeAccountNo(v) {
  return String(v || '').replace(/\s+/g, '');
}

export function normalizeCardNo(v) {
  return String(v || '').replace(/[\s-]/g, '');
}

/** 카드번호 표시·저장 — 숫자만 추출 후 4자리 하이픈 (최대 16자리) */
export function formatCardNo(v) {
  const digits = normalizeCardNo(v).replace(/\D/g, '').slice(0, 16);
  if (!digits) return '';
  const parts = [];
  for (let i = 0; i < digits.length; i += 4) {
    parts.push(digits.slice(i, i + 4));
  }
  return parts.join('-');
}

export function maskAccountNo(no) {
  const s = normalizeAccountNo(no);
  if (s.length <= 4) return s;
  return `${'*'.repeat(Math.min(s.length - 4, 8))}${s.slice(-4)}`;
}

export function maskCardNo(no) {
  const s = normalizeCardNo(no);
  if (s.length <= 4) return s;
  return `****-****-****-${s.slice(-4)}`;
}

export function nextSortOrderBy10(rows = []) {
  const max = (rows || [])
    .filter((r) => r.mod !== 'D')
    .reduce((m, r) => Math.max(m, Number(r.sortOrder) || 0), 0);
  return max + 10;
}

/** 코드·명칭 배열 정규화 (빈값 제거, 길이 맞춤) */
export function normalizeCodeNameLists(codes = [], names = []) {
  const c = (codes || []).map((x) => String(x || '').trim());
  const n = (names || []).map((x) => String(x || '').trim());
  const outC = [];
  const outN = [];
  const seen = new Set();
  const len = Math.max(c.length, n.length);
  for (let i = 0; i < len; i += 1) {
    const code = c[i] || '';
    const name = n[i] || '';
    if (!code && !name) continue;
    const key = code || `N:${name}`;
    if (seen.has(key)) continue;
    seen.add(key);
    outC.push(code);
    outN.push(name || code);
  }
  return { codes: outC, names: outN };
}

/** 셀 표시: `기획팀` / `기획팀 외 2건` · 빈배열 = 빈문자(제한 없음) */
export function summarizeMultiNames(names = []) {
  const list = (names || []).map((s) => String(s || '').trim()).filter(Boolean);
  if (!list.length) return '';
  if (list.length === 1) return list[0];
  return `${list[0]} 외 ${list.length - 1}건`;
}

/** 호버 title: 목록 전체 */
export function multiSelectionTooltip(codes = [], names = [], emptyText = '제한 없음(전체)') {
  const { codes: c, names: n } = normalizeCodeNameLists(codes, names);
  if (!c.length && !n.length) return emptyText;
  const lines = c.map((code, i) => {
    const name = n[i] || '';
    if (code && name && code !== name) return `· ${code} ${name}`;
    return `· ${name || code}`;
  });
  return `선택 ${lines.length}건\n${lines.join('\n')}`;
}

export function emptyAccount(overrides = {}) {
  return {
    accountUid: '',
    accountCode: '',
    accountName: '',
    bankCode: '',
    bankName: '',
    accountNo: '',
    accountHolder: '',
    purpose: ACCOUNT_PURPOSE.EXPENSE,
    deptCodes: [],
    deptNames: [],
    projectCodes: [],
    projectNames: [],
    useYn: 'Y',
    sortOrder: 10,
    remark: '',
    mod: '',
    ...overrides,
  };
}

export function emptyCard(overrides = {}) {
  return {
    cardUid: '',
    cardCode: '',
    cardName: '',
    cardNo: '',
    cardType: CARD_TYPE.CREDIT,
    /** 사용용도(사업비·관리비·유류비 등) — 결의 승인내역 그룹 필터 */
    cardUsage: CARD_USAGE.PROJECT,
    /** 유효기간 — 단순 텍스트 (예: 12/28, 2028-12) */
    expireText: '',
    accountUid: '',
    issuerName: '',
    useYn: 'Y',
    sortOrder: 10,
    remark: '',
    mod: '',
    ...overrides,
  };
}

function migrateAccountShape(row) {
  if (!row) return row;
  const depts = normalizeCodeNameLists(row.deptCodes, row.deptNames);
  const projects = normalizeCodeNameLists(row.projectCodes, row.projectNames);
  return {
    ...row,
    deptCodes: depts.codes,
    deptNames: depts.names,
    projectCodes: projects.codes,
    projectNames: projects.names,
  };
}

/** 레거시 스코프 행 → 계좌 부서/사업 합집합 이관 */
function absorbLegacyScopes(accounts, tenantCode) {
  const key = `${ORG_FINANCE_SCOPE_PREFIX}|${tenantCode || ''}`;
  const scopes = (readJson(key, []) || []).filter(
    (s) => s && s.mod !== 'D' && s.ownerType === 'ACCOUNT'
  );
  if (!scopes.length) return accounts;

  const byUid = new Map();
  scopes.forEach((s) => {
    const uid = s.ownerUid;
    if (!uid) return;
    if (!byUid.has(uid)) byUid.set(uid, { depts: [], projects: [] });
    const bucket = byUid.get(uid);
    if (s.deptCode || s.deptName) {
      bucket.depts.push({ code: s.deptCode || '', name: s.deptName || '' });
    }
    if (s.projectCode || s.projectName) {
      bucket.projects.push({ code: s.projectCode || '', name: s.projectName || '' });
    }
  });

  return (accounts || []).map((a) => {
    const extra = byUid.get(a.accountUid);
    if (!extra) return migrateAccountShape(a);
    const depts = normalizeCodeNameLists(
      [...(a.deptCodes || []), ...extra.depts.map((d) => d.code)],
      [...(a.deptNames || []), ...extra.depts.map((d) => d.name)]
    );
    const projects = normalizeCodeNameLists(
      [...(a.projectCodes || []), ...extra.projects.map((p) => p.code)],
      [...(a.projectNames || []), ...extra.projects.map((p) => p.name)]
    );
    return migrateAccountShape({
      ...a,
      deptCodes: depts.codes,
      deptNames: depts.names,
      projectCodes: projects.codes,
      projectNames: projects.names,
    });
  });
}

export function getCachedOrgAccounts(tenantCode) {
  return cacheGet(orgAccountCache, tenantCode) || [];
}

export function getCachedOrgCards(tenantCode) {
  return cacheGet(orgCardCache, tenantCode) || [];
}

export async function loadOrgAccounts(tenantCode) {
  const tc = tenantCode || '';
  if (isBdApiEnabled()) {
    const rows = await commonSelect('CmOrgFinance.selectOrgAccountList', { tenantCode: tc });
    const mapped = (rows || []).map(mapOrgAccountFromApi);
    cacheSet(orgAccountCache, tc, mapped);
    return mapped;
  }
  const raw = (readJson(orgAccountKey(tc), []) || []).filter((r) => r && r.mod !== 'D');
  const mapped = absorbLegacyScopes(raw.map(migrateAccountShape), tc);
  cacheSet(orgAccountCache, tc, mapped);
  return mapped;
}

export async function loadOrgCards(tenantCode) {
  const tc = tenantCode || '';
  if (isBdApiEnabled()) {
    const rows = await commonSelect('CmOrgFinance.selectOrgCardList', { tenantCode: tc });
    const mapped = (rows || []).map((c) => ({
      ...c,
      cardNo: normalizeCardNo(c.cardNo),
      mod: '',
    }));
    cacheSet(orgCardCache, tc, mapped);
    return mapped;
  }
  const mapped = (readJson(orgCardKey(tc), []) || [])
    .filter((r) => r && r.mod !== 'D')
    .map((c) => ({
      ...c,
      cardNo: normalizeCardNo(c.cardNo),
      mod: '',
    }));
  cacheSet(orgCardCache, tc, mapped);
  return mapped;
}

export async function saveOrgFinanceBundle(tenantCode, { accounts, cards }) {
  const tc = tenantCode || '';
  const nextAccounts = (accounts || [])
    .filter((a) => a && a.mod !== 'D')
    .map((a) => {
      const depts = normalizeCodeNameLists(a.deptCodes, a.deptNames);
      const projects = normalizeCodeNameLists(a.projectCodes, a.projectNames);
      return {
        ...a,
        accountNo: normalizeAccountNo(a.accountNo),
        deptCodes: depts.codes,
        deptNames: depts.names,
        projectCodes: projects.codes,
        projectNames: projects.names,
      };
    });
  const nextCards = (cards || [])
    .filter((c) => c && c.mod !== 'D')
    .map((c) => ({
      ...c,
      cardNo: normalizeCardNo(c.cardNo),
      cardUsage: c.cardUsage || CARD_USAGE.PROJECT,
      expireText: String(c.expireText || '').trim(),
    }));

  if (isBdApiEnabled()) {
    const prevA = getCachedOrgAccounts(tc).length
      ? getCachedOrgAccounts(tc)
      : await loadOrgAccounts(tc);
    const prevC = getCachedOrgCards(tc).length ? getCachedOrgCards(tc) : await loadOrgCards(tc);
    const dirtyA = diffRowsByUid(prevA, nextAccounts, 'accountUid', () => createAccountUid()).map(
      (r) => (r.mod === 'D' ? r : mapOrgAccountToApi(r))
    );
    const deletedAccountUids = new Set(dirtyA.filter((r) => r.mod === 'D').map((r) => r.accountUid));
    const cardsForDiff = nextCards.filter((c) => !deletedAccountUids.has(c.accountUid));
    const dirtyC = [
      ...diffRowsByUid(prevC, cardsForDiff, 'cardUid', () => createCardUid()),
      ...prevC.filter((c) => deletedAccountUids.has(c.accountUid)).map((c) => ({ ...c, mod: 'D' })),
    ];
    const delC = dirtyC.filter((r) => r.mod === 'D');
    const delA = dirtyA.filter((r) => r.mod === 'D');
    const upsA = dirtyA.filter((r) => r.mod === 'A' || r.mod === 'U');
    const upsC = dirtyC.filter((r) => r.mod === 'A' || r.mod === 'U');
    if (delC.length) await commonSaveList(SAVE_QUERY_ORG_CARD, delC, { tenantCode: tc });
    if (delA.length) await commonSaveList(SAVE_QUERY_ORG_ACCOUNT, delA, { tenantCode: tc });
    if (upsA.length) await commonSaveList(SAVE_QUERY_ORG_ACCOUNT, upsA, { tenantCode: tc });
    if (upsC.length) await commonSaveList(SAVE_QUERY_ORG_CARD, upsC, { tenantCode: tc });
    return {
      accounts: await loadOrgAccounts(tc),
      cards: await loadOrgCards(tc),
    };
  }

  writeJson(orgAccountKey(tc), nextAccounts.map((a) => ({ ...a, mod: '' })));
  writeJson(orgCardKey(tc), nextCards.map((c) => ({ ...c, mod: '' })));
  try {
    localStorage.removeItem(`${ORG_FINANCE_SCOPE_PREFIX}|${tc}`);
  } catch {
    /* ignore */
  }
  cacheSet(orgAccountCache, tc, nextAccounts.map((a) => ({ ...a, mod: '' })));
  cacheSet(orgCardCache, tc, nextCards.map((c) => ({ ...c, mod: '' })));
  return { accounts: nextAccounts, cards: nextCards };
}

export function accountLabelOf(accounts = [], accountUid) {
  const a = (accounts || []).find((r) => r.accountUid === accountUid);
  if (!a) return '';
  return `${a.accountName || a.accountCode || ''} (${a.bankName || ''} · ${maskAccountNo(a.accountNo)})`.trim();
}

export function enrichAccountRow(row) {
  const a = migrateAccountShape(row);
  return {
    ...a,
    purposeNm: accountPurposeLabel(a.purpose),
    accountNoDisp: maskAccountNo(a.accountNo),
    deptDisp: summarizeMultiNames(a.deptNames),
    projectDisp: summarizeMultiNames(a.projectNames),
    deptTitle: multiSelectionTooltip(a.deptCodes, a.deptNames, '부서 제한 없음(전체)'),
    projectTitle: multiSelectionTooltip(a.projectCodes, a.projectNames, '사업 제한 없음(전체)'),
  };
}

export function enrichCardRow(row, accounts = []) {
  const acc = (accounts || []).find((a) => a.accountUid === row.accountUid);
  const enrichedAcc = acc ? enrichAccountRow(acc) : null;
  const digits = normalizeCardNo(row.cardNo);
  return {
    ...row,
    // 그리드 편집·표시용 하이픈 · 저장 시 normalizeCardNo
    cardNo: formatCardNo(digits),
    cardUsage: row.cardUsage || '',
    expireText: row.expireText || '',
    cardTypeNm: cardTypeLabel(row.cardType),
    cardUsageNm: cardUsageLabel(row.cardUsage),
    cardNoDisp: maskCardNo(digits),
    accountName: enrichedAcc?.accountName || '',
    accountNoDisp: enrichedAcc ? maskAccountNo(enrichedAcc.accountNo) : '',
    bankName: enrichedAcc?.bankName || '',
    deptDisp: enrichedAcc?.deptDisp || '',
    projectDisp: enrichedAcc?.projectDisp || '',
    deptTitle: enrichedAcc?.deptTitle || '연결 계좌 없음',
    projectTitle: enrichedAcc?.projectTitle || '연결 계좌 없음',
  };
}

export function filterOrgAccounts(rows = [], { keyword, purpose, useYn } = {}) {
  let list = [...(rows || [])];
  const kw = String(keyword || '').trim().toLowerCase();
  if (kw) {
    list = list.filter((r) => {
      const hay = [
        r.accountCode,
        r.accountName,
        r.bankCode,
        r.bankName,
        r.accountNo,
        r.accountHolder,
        accountPurposeLabel(r.purpose),
        ...(r.deptNames || []),
        ...(r.projectNames || []),
        r.remark,
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(kw);
    });
  }
  if (purpose) list = list.filter((r) => r.purpose === purpose);
  if (useYn) list = list.filter((r) => r.useYn === useYn);
  return list;
}

export function filterOrgCards(rows = [], { keyword, cardType, cardUsage, useYn, accountUid } = {}) {
  let list = [...(rows || [])];
  const kw = String(keyword || '').trim().toLowerCase();
  if (kw) {
    list = list.filter((r) => {
      const hay = [
        r.cardCode,
        r.cardName,
        r.cardNo,
        r.issuerName,
        cardTypeLabel(r.cardType),
        cardUsageLabel(r.cardUsage),
        r.expireText,
        r.remark,
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(kw);
    });
  }
  if (cardType) list = list.filter((r) => r.cardType === cardType);
  if (cardUsage) list = list.filter((r) => r.cardUsage === cardUsage);
  if (useYn) list = list.filter((r) => r.useYn === useYn);
  if (accountUid) list = list.filter((r) => r.accountUid === accountUid);
  return list;
}

/**
 * 결의 채주 등 — 부서·사업 컨텍스트로 계좌 후보
 * 부서/사업 배열이 비어 있으면 해당 축은 제한 없음
 */
export function filterAccountsForContext(
  accounts = [],
  { purposeAllowed, deptCode, projectCode, useYn = 'Y' } = {}
) {
  const allowed =
    purposeAllowed instanceof Set
      ? purposeAllowed
      : purposeAllowed
        ? new Set(purposeAllowed)
        : null;

  const d = String(deptCode || '').trim();
  const p = String(projectCode || '').trim();

  const live = (accounts || []).filter((a) => {
    if (a.mod === 'D') return false;
    if (useYn && a.useYn !== useYn) return false;
    if (allowed && !allowed.has(a.purpose)) return false;
    return true;
  });

  const scored = live.map((a) => {
    const depts = (a.deptCodes || []).map((x) => String(x || '').trim()).filter(Boolean);
    const projects = (a.projectCodes || []).map((x) => String(x || '').trim()).filter(Boolean);

    if (depts.length && d && !depts.includes(d)) {
      return { row: a, score: -1, matched: false };
    }
    if (projects.length && p && !projects.includes(p)) {
      return { row: a, score: -1, matched: false };
    }

    let score = 0;
    if (!depts.length && !projects.length) score = 0; // 전 기관 공용
    if (depts.length && d && depts.includes(d)) score += 10;
    if (projects.length && p && projects.includes(p)) score += 20;
    if (depts.length && !d) score += 1; // 부서 미지정 조회 시 제한 계좌는 후순위
    if (projects.length && !p) score += 1;
    return { row: a, score, matched: true };
  });

  return scored
    .filter((x) => x.matched)
    .sort((a, b) => b.score - a.score || (a.row.sortOrder || 0) - (b.row.sortOrder || 0))
    .map((x) => x.row);
}

/** 카드 → 연결 계좌의 부서·사업 (없으면 빈 = 제한 없음) */
export function resolveCardAxisFromAccount(card, accounts = []) {
  const acc = (accounts || []).find((a) => a.accountUid === card?.accountUid);
  if (!acc) {
    return { deptCodes: [], deptNames: [], projectCodes: [], projectNames: [] };
  }
  const a = migrateAccountShape(acc);
  return {
    deptCodes: [...(a.deptCodes || [])],
    deptNames: [...(a.deptNames || [])],
    projectCodes: [...(a.projectCodes || [])],
    projectNames: [...(a.projectNames || [])],
  };
}

export function findCardByNo(cards = [], cardNo) {
  const key = normalizeCardNo(cardNo);
  if (!key) return null;
  return (cards || []).find((c) => normalizeCardNo(c.cardNo) === key && c.mod !== 'D') || null;
}

export function validateOrgFinanceBundle(accounts = [], cards = []) {
  const errors = [];
  const accNos = new Map();
  const accCodes = new Map();

  (accounts || [])
    .filter((a) => a.mod !== 'D')
    .forEach((a, i) => {
      const label = a.accountName || a.accountCode || `#${i + 1}`;
      if (!String(a.accountName || '').trim()) errors.push(`계좌 [${label}]: 계좌명 필수`);
      if (!normalizeAccountNo(a.accountNo)) errors.push(`계좌 [${label}]: 계좌번호 필수`);
      if (!a.purpose) errors.push(`계좌 [${label}]: 용도 필수`);
      const no = normalizeAccountNo(a.accountNo);
      if (no) {
        if (accNos.has(no)) errors.push(`계좌번호 중복: ${no}`);
        else accNos.set(no, a.accountUid);
      }
      const code = String(a.accountCode || '').trim();
      if (code) {
        if (accCodes.has(code)) errors.push(`계좌코드 중복: ${code}`);
        else accCodes.set(code, a.accountUid);
      }
    });

  const accUidSet = new Set(
    (accounts || []).filter((a) => a.mod !== 'D').map((a) => a.accountUid)
  );
  const cardNos = new Map();

  (cards || [])
    .filter((c) => c.mod !== 'D')
    .forEach((c, i) => {
      const label = c.cardName || c.cardCode || `#${i + 1}`;
      if (!String(c.cardName || '').trim()) errors.push(`카드 [${label}]: 카드명 필수`);
      if (!normalizeCardNo(c.cardNo)) errors.push(`카드 [${label}]: 카드번호 필수`);
      if (!c.cardType) errors.push(`카드 [${label}]: 카드종류 필수`);
      if (!c.accountUid) errors.push(`카드 [${label}]: 연결 계좌 필수`);
      else if (!accUidSet.has(c.accountUid)) {
        errors.push(`카드 [${label}]: 연결 계좌가 없습니다`);
      }
      const no = normalizeCardNo(c.cardNo);
      if (no) {
        if (cardNos.has(no)) errors.push(`카드번호 중복: ${maskCardNo(no)}`);
        else cardNos.set(no, c.cardUid);
      }
    });

  return errors;
}

export function nextAccountCode(rows = []) {
  const nums = (rows || [])
    .map((r) => String(r.accountCode || '').replace(/\D/g, ''))
    .map((n) => Number(n) || 0);
  const next = Math.max(0, ...nums) + 1;
  return `A${String(next).padStart(3, '0')}`;
}

export function nextCardCode(rows = []) {
  const nums = (rows || [])
    .map((r) => String(r.cardCode || '').replace(/\D/g, ''))
    .map((n) => Number(n) || 0);
  const next = Math.max(0, ...nums) + 1;
  return `C${String(next).padStart(3, '0')}`;
}

export async function ensureOrgFinanceSeed(tenantCode) {
  if (isBdApiEnabled()) {
    await loadOrgAccounts(tenantCode);
    await loadOrgCards(tenantCode);
    return { seeded: false };
  }
  const existing = await loadOrgAccounts(tenantCode);
  if (existing.length) return { seeded: false };

  const a1 = createAccountUid();
  const a2 = createAccountUid();
  const a3 = createAccountUid();
  const c1 = createCardUid();
  const c2 = createCardUid();

  const accounts = [
    emptyAccount({
      accountUid: a1,
      accountCode: 'A001',
      accountName: '본원 지출보통',
      bankCode: '004',
      bankName: '국민은행',
      accountNo: '12345678901234',
      accountHolder: '샘플기관',
      purpose: ACCOUNT_PURPOSE.EXPENSE,
      sortOrder: 10,
      useYn: 'Y',
      // 부서·사업 모두 null = 전 기관 공용
    }),
    emptyAccount({
      accountUid: a2,
      accountCode: 'A002',
      accountName: '수입전용',
      bankCode: '088',
      bankName: '신한은행',
      accountNo: '110123456789',
      accountHolder: '샘플기관',
      purpose: ACCOUNT_PURPOSE.INCOME,
      sortOrder: 20,
      useYn: 'Y',
    }),
    emptyAccount({
      accountUid: a3,
      accountCode: 'A003',
      accountName: '보조금 입출금',
      bankCode: '011',
      bankName: '농협',
      accountNo: '3012345678901',
      accountHolder: '샘플기관',
      purpose: ACCOUNT_PURPOSE.INOUT,
      sortOrder: 30,
      useYn: 'Y',
      // 예시: a사업만 매핑·부서 null → 해당 사업 접근 부서 모두 집행 가능
      projectCodes: ['P-SUBSIDY', 'P-SUBSIDY-B'],
      projectNames: ['보조금사업A', '보조금사업B'],
      remark: '사업만 매핑(부서 제한 없음)',
    }),
  ];

  const cards = [
    emptyCard({
      cardUid: c1,
      cardCode: 'C001',
      cardName: '기획팀 공용신한',
      cardNo: '9410111122223333',
      cardType: CARD_TYPE.CREDIT,
      cardUsage: CARD_USAGE.PROJECT,
      expireText: '12/28',
      accountUid: a3,
      issuerName: '신한카드',
      sortOrder: 10,
      useYn: 'Y',
    }),
    emptyCard({
      cardUid: c2,
      cardCode: 'C002',
      cardName: '총무 유류체크',
      cardNo: '5365123456789012',
      cardType: CARD_TYPE.CHECK,
      cardUsage: CARD_USAGE.FUEL,
      expireText: '06/27',
      accountUid: a1,
      issuerName: '국민카드',
      sortOrder: 20,
      useYn: 'Y',
    }),
  ];

  await saveOrgFinanceBundle(tenantCode, { accounts, cards });
  return { seeded: true };
}

// ─── 엑셀 ─────────────────────────────────────────────────────────

export const ORG_ACCOUNT_EXCEL_HEADERS = [
  '계좌코드',
  '계좌명',
  '용도',
  '은행코드',
  '은행명',
  '계좌번호',
  '예금주',
  '사용',
  '정렬',
  '비고',
  '부서코드',
  '부서명',
  '사업코드',
  '사업명',
];

/** 카드 시트 — 사업/부서 열 없음(계좌 연결만) · 용도·유효기간 포함 */
export const ORG_CARD_EXCEL_HEADERS = [
  '카드코드',
  '카드명',
  '카드종류',
  '용도',
  '카드번호',
  '유효기간',
  '연결계좌코드',
  '연결계좌번호',
  '카드사',
  '사용',
  '정렬',
  '비고',
];

function cell(row, idx) {
  if (idx < 0 || idx == null) return '';
  const v = row?.[idx];
  if (v == null) return '';
  return String(v).trim();
}

function headerIndexMap(headerRow = []) {
  const map = {};
  (headerRow || []).forEach((h, i) => {
    const key = String(h || '').trim();
    if (key) map[key] = i;
  });
  return map;
}

function resolvePurpose(raw) {
  const s = String(raw || '').trim();
  if (!s) return ACCOUNT_PURPOSE.EXPENSE;
  const byVal = ACCOUNT_PURPOSE_OPTIONS.find((o) => o.value === s);
  if (byVal) return byVal.value;
  const byLabel = ACCOUNT_PURPOSE_OPTIONS.find((o) => o.label === s);
  return byLabel?.value || ACCOUNT_PURPOSE.ETC;
}

function resolveCardType(raw) {
  const s = String(raw || '').trim();
  if (!s) return CARD_TYPE.CREDIT;
  const byVal = CARD_TYPE_OPTIONS.find((o) => o.value === s);
  if (byVal) return byVal.value;
  const byLabel = CARD_TYPE_OPTIONS.find((o) => o.label === s);
  return byLabel?.value || CARD_TYPE.ETC;
}

function resolveCardUsage(raw) {
  const s = String(raw || '').trim();
  if (!s) return CARD_USAGE.PROJECT;
  const byVal = CARD_USAGE_OPTIONS.find((o) => o.value === s);
  if (byVal) return byVal.value;
  const byLabel = CARD_USAGE_OPTIONS.find((o) => o.label === s);
  return byLabel?.value || CARD_USAGE.ETC;
}

function resolveYn(raw, fallback = 'Y') {
  const s = String(raw || '').trim().toUpperCase();
  if (!s) return fallback;
  if (s === 'Y' || s === 'N') return s;
  if (s === '사용' || s === '예' || s === 'TRUE' || s === '1') return 'Y';
  if (s === '미사용' || s === '아니오' || s === 'FALSE' || s === '0') return 'N';
  return fallback;
}

export function readExcelSheetsFromArrayBuffer(buf) {
  const wb = XLSX.read(buf, { type: 'array' });
  const sheets = {};
  (wb.SheetNames || []).forEach((name) => {
    sheets[name] = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: '' });
  });
  return { sheetNames: wb.SheetNames || [], sheets };
}

function detectSheetKind(aoa) {
  const hdr = (aoa?.[0] || []).map((h) => String(h || '').trim());
  if (hdr.includes('카드번호') || hdr.includes('카드명')) return 'card';
  if (hdr.includes('계좌번호') || hdr.includes('계좌명')) return 'account';
  return '';
}

export function downloadOrgFinanceExcelTemplate(fileName = '기관계좌카드업로드양식.xlsx') {
  const accSample = [
    ORG_ACCOUNT_EXCEL_HEADERS,
    // 동일 계좌·사업A · 부서 비움 → 사업만 매핑(부서 제한 없음)
    [
      'A003',
      '보조금 입출금',
      '입출금',
      '011',
      '농협',
      '3012345678901',
      '샘플기관',
      'Y',
      '30',
      '사업만 매핑',
      '',
      '',
      'P-SUBSIDY',
      '보조금사업A',
    ],
    // 같은 계좌에 사업B 추가행 → 사업 합집합
    [
      'A003',
      '보조금 입출금',
      '입출금',
      '011',
      '농협',
      '3012345678901',
      '샘플기관',
      'Y',
      '30',
      '',
      '',
      '',
      'P-SUBSIDY-B',
      '보조금사업B',
    ],
    // 부서1만 + 사업A (부서 제한 예시) — 별도 계좌
    [
      'A004',
      '보조금(1부서전용)',
      '입출금',
      '011',
      '농협',
      '3019999888877',
      '샘플기관',
      'Y',
      '40',
      '부서1만 집행',
      'D001',
      '1부서',
      'P-SUBSIDY',
      '보조금사업A',
    ],
    // 공용 계좌 (부서·사업 모두 빈칸)
    [
      'A001',
      '본원 지출보통',
      '지출',
      '004',
      '국민은행',
      '12345678901234',
      '샘플기관',
      'Y',
      '10',
      '공용',
      '',
      '',
      '',
      '',
    ],
  ];
  const cardSample = [
    ORG_CARD_EXCEL_HEADERS,
    [
      'C001',
      '기획팀 공용신한',
      '신용카드',
      '사업비',
      '9410-1111-2222-3333',
      '12/28',
      'A003',
      '3012345678901',
      '신한카드',
      'Y',
      '10',
      '계좌 사업이 조인됨',
    ],
    [
      'C002',
      '총무 유류체크',
      '체크카드',
      '유류비',
      '5365-1234-5678-9012',
      '06/27',
      'A001',
      '12345678901234',
      '국민카드',
      'Y',
      '20',
      '',
    ],
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(accSample), '기관계좌');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(cardSample), '기관카드');
  XLSX.writeFile(wb, fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`);
}

/**
 * 계좌 평탄 시트 — 동일 계좌의 부서·사업 행을 각각 합집합으로 병합
 */
export function parseFlatOrgAccountExcel(aoa) {
  const errors = [];
  if (!aoa?.length) return { accounts: [], errors: ['빈 시트입니다.'] };
  const idx = headerIndexMap(aoa[0]);
  ['계좌명', '계좌번호'].forEach((h) => {
    if (idx[h] == null) errors.push(`필수 열 없음: ${h}`);
  });
  if (errors.length) return { accounts: [], errors };

  const groupMap = new Map();
  for (let r = 1; r < aoa.length; r += 1) {
    const row = aoa[r];
    if (!row || !row.some((c) => String(c || '').trim())) continue;
    const accountNo = normalizeAccountNo(cell(row, idx['계좌번호']));
    const accountCode = cell(row, idx['계좌코드']);
    const accountName = cell(row, idx['계좌명']);
    if (!accountNo && !accountName) continue;
    const groupKey = accountNo || `CODE:${accountCode}` || `R${r}`;

    if (!groupMap.has(groupKey)) {
      groupMap.set(groupKey, {
        account: emptyAccount({
          accountUid: createAccountUid(),
          accountCode,
          accountName,
          purpose: resolvePurpose(cell(row, idx['용도'])),
          bankCode: cell(row, idx['은행코드']),
          bankName: cell(row, idx['은행명']),
          accountNo,
          accountHolder: cell(row, idx['예금주']),
          useYn: resolveYn(cell(row, idx['사용']), 'Y'),
          sortOrder: Number(cell(row, idx['정렬'])) || 10,
          remark: cell(row, idx['비고']),
          deptCodes: [],
          deptNames: [],
          projectCodes: [],
          projectNames: [],
          mod: 'A',
        }),
      });
    }
    const g = groupMap.get(groupKey);
    const deptCode = cell(row, idx['부서코드']);
    const deptName = cell(row, idx['부서명']);
    const projectCode = cell(row, idx['사업코드']);
    const projectName = cell(row, idx['사업명']);
    if (deptCode || deptName) {
      g.account.deptCodes.push(deptCode);
      g.account.deptNames.push(deptName || deptCode);
    }
    if (projectCode || projectName) {
      g.account.projectCodes.push(projectCode);
      g.account.projectNames.push(projectName || projectCode);
    }
  }

  const accounts = [];
  let sort = 10;
  groupMap.forEach((g) => {
    if (!g.account.accountCode) g.account.accountCode = nextAccountCode(accounts);
    if (!g.account.sortOrder) g.account.sortOrder = sort;
    sort += 10;
    const depts = normalizeCodeNameLists(g.account.deptCodes, g.account.deptNames);
    const projects = normalizeCodeNameLists(g.account.projectCodes, g.account.projectNames);
    accounts.push({
      ...g.account,
      deptCodes: depts.codes,
      deptNames: depts.names,
      projectCodes: projects.codes,
      projectNames: projects.names,
    });
  });
  return { accounts, errors };
}

export function parseFlatOrgCardExcel(aoa) {
  const errors = [];
  if (!aoa?.length) return { cards: [], errors: ['빈 시트입니다.'] };
  const idx = headerIndexMap(aoa[0]);
  ['카드명', '카드번호'].forEach((h) => {
    if (idx[h] == null) errors.push(`필수 열 없음: ${h}`);
  });
  if (errors.length) return { cards: [], errors };

  const seen = new Map();
  for (let r = 1; r < aoa.length; r += 1) {
    const row = aoa[r];
    if (!row || !row.some((c) => String(c || '').trim())) continue;
    const cardNo = normalizeCardNo(cell(row, idx['카드번호']));
    const cardCode = cell(row, idx['카드코드']);
    const cardName = cell(row, idx['카드명']);
    if (!cardNo && !cardName) continue;
    const groupKey = cardNo || `CODE:${cardCode}` || `R${r}`;
    if (seen.has(groupKey)) continue;
    seen.set(
      groupKey,
      emptyCard({
        cardUid: createCardUid(),
        cardCode,
        cardName,
        cardType: resolveCardType(cell(row, idx['카드종류'])),
        cardUsage: resolveCardUsage(cell(row, idx['용도'])),
        cardNo: normalizeCardNo(cardNo),
        expireText: cell(row, idx['유효기간']),
        accountUid: '',
        _linkAccountCode: cell(row, idx['연결계좌코드']),
        _linkAccountNo: normalizeAccountNo(cell(row, idx['연결계좌번호'])),
        issuerName: cell(row, idx['카드사']),
        useYn: resolveYn(cell(row, idx['사용']), 'Y'),
        sortOrder: Number(cell(row, idx['정렬'])) || 10,
        remark: cell(row, idx['비고']),
        mod: 'A',
      })
    );
  }

  const cards = [];
  let sort = 10;
  seen.forEach((c) => {
    if (!c.cardCode) c.cardCode = nextCardCode(cards);
    if (!c.sortOrder) c.sortOrder = sort;
    sort += 10;
    cards.push(c);
  });
  return { cards, errors };
}

export function parseOrgFinanceExcelWorkbook(buf) {
  const { sheetNames, sheets } = readExcelSheetsFromArrayBuffer(buf);
  const errors = [];
  let accounts = [];
  let cards = [];

  const parseOne = (aoa, forcedKind) => {
    const kind = forcedKind || detectSheetKind(aoa);
    if (kind === 'account') {
      const parsed = parseFlatOrgAccountExcel(aoa);
      errors.push(...(parsed.errors || []));
      accounts = [...accounts, ...(parsed.accounts || [])];
    } else if (kind === 'card') {
      const parsed = parseFlatOrgCardExcel(aoa);
      errors.push(...(parsed.errors || []));
      cards = [...cards, ...(parsed.cards || [])];
    } else {
      errors.push('시트 형식을 알 수 없습니다. 계좌명/계좌번호 또는 카드명/카드번호 열이 필요합니다.');
    }
  };

  if (sheetNames.includes('기관계좌') || sheetNames.includes('기관카드')) {
    if (sheets['기관계좌']?.length) parseOne(sheets['기관계좌'], 'account');
    if (sheets['기관카드']?.length) parseOne(sheets['기관카드'], 'card');
  } else if (sheetNames.length) {
    parseOne(sheets[sheetNames[0]], '');
  }

  return { accounts, cards, errors };
}

export function mergeUploadedOrgFinance(existingAccounts, existingCards, uploaded) {
  const accounts = [...(existingAccounts || [])].map(migrateAccountShape);
  const cards = [...(existingCards || [])];
  const uidRemapAcc = new Map();

  const findAcc = (up) => {
    const no = normalizeAccountNo(up.accountNo);
    const code = String(up.accountCode || '').trim();
    return accounts.findIndex((a) => {
      if (a.mod === 'D') return false;
      if (no && normalizeAccountNo(a.accountNo) === no) return true;
      if (code && String(a.accountCode || '').trim() === code) return true;
      return false;
    });
  };

  (uploaded.accounts || []).forEach((up) => {
    const idx = findAcc(up);
    const depts = normalizeCodeNameLists(up.deptCodes, up.deptNames);
    const projects = normalizeCodeNameLists(up.projectCodes, up.projectNames);
    const payload = {
      ...up,
      deptCodes: depts.codes,
      deptNames: depts.names,
      projectCodes: projects.codes,
      projectNames: projects.names,
    };
    if (idx >= 0) {
      const uid = accounts[idx].accountUid;
      uidRemapAcc.set(up.accountUid, uid);
      accounts[idx] = {
        ...accounts[idx],
        ...payload,
        accountUid: uid,
        mod: accounts[idx].mod === 'A' ? 'A' : 'U',
      };
    } else {
      uidRemapAcc.set(up.accountUid, up.accountUid);
      accounts.push({ ...payload, mod: 'A' });
    }
  });

  const resolveAccountUid = (code, no) => {
    const c = String(code || '').trim();
    const n = normalizeAccountNo(no);
    const hit = accounts.find((a) => {
      if (a.mod === 'D') return false;
      if (n && normalizeAccountNo(a.accountNo) === n) return true;
      if (c && String(a.accountCode || '').trim() === c) return true;
      return false;
    });
    return hit?.accountUid || '';
  };

  (uploaded.cards || []).forEach((up) => {
    const linkUid =
      resolveAccountUid(up._linkAccountCode, up._linkAccountNo) || up.accountUid || '';
    const no = normalizeCardNo(up.cardNo);
    const code = String(up.cardCode || '').trim();
    const idx = cards.findIndex((c) => {
      if (c.mod === 'D') return false;
      if (no && normalizeCardNo(c.cardNo) === no) return true;
      if (code && String(c.cardCode || '').trim() === code) return true;
      return false;
    });
    const cleaned = { ...up, cardNo: no };
    delete cleaned._linkAccountCode;
    delete cleaned._linkAccountNo;
    if (idx >= 0) {
      cards[idx] = {
        ...cards[idx],
        ...cleaned,
        cardUid: cards[idx].cardUid,
        cardNo: no,
        accountUid: linkUid || cards[idx].accountUid,
        mod: cards[idx].mod === 'A' ? 'A' : 'U',
      };
    } else {
      cards.push({ ...cleaned, accountUid: linkUid, cardNo: no, mod: 'A' });
    }
  });

  return { accounts, cards };
}
